/**
 * App.tsx
 * 
 * Main part. Sets up the Fluent UI provider and
 * renders the core application components. Handles the main layout and
 * styling of the application interface.
 */
/*! Copyright Jon Verrier 2025 */

// React
import React, { useState } from 'react';

// Fluent
import {
   Text,
   Link as FluentLink,
   Spinner,
   RadioGroup,
   Radio,
   Label,
} from '@fluentui/react-components';

import { IMultilineEditProps, MultilineEdit } from './MultilineEdit';
import { ELinterMode, getUIStrings } from './UIStrings';
import { standardTextStyles, standardLinkStyles, standardColumnElementStyles, standardRowElementStyles} from './CommonStyles';
import { CopyableText } from './CopyableText';
import { Message, MessageIntent } from './Message';
import { LinterUIStateMachine, EUIState, EApiEvent } from './UIStateMachine';
import { processRequirement } from './Call';
import { ISpecificationEvaluation } from '../import/RequirementsLinterApiTypes';
import { pageOuterStyles, innerColumnStyles } from './OuterStyles';
import { Spacer, Footer } from './SiteUtilities';

const kFontNameForTextWrapCalculation = "12pt Segoe UI";
const kRequirementMaxLength = 4096;

/** Initial linter mode (user story vs requirement); user can change via toggle. */
export interface IAppProps {
   linterMode: ELinterMode;
}

const kLintAsLabel = "Lint as:";
const kRequirementOptionLabel = "Requirement";
const kUserStoryOptionLabel = "User story";

// Loca version that works in browser
//https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid
function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

const sessionUuid = uuidv4();

export const App = (props: IAppProps) => {
   
   const pageOuterClasses = pageOuterStyles();
   const innerColumnClasses = innerColumnStyles();
   const columnElementClasses = standardColumnElementStyles();
   const textClasses = standardTextStyles();   
   const linkClasses = standardLinkStyles();

   const [linterMode, setLinterMode] = useState<ELinterMode>(props.linterMode);
   const uiStrings = getUIStrings(linterMode);

   let [state, setState] = useState<LinterUIStateMachine>(new LinterUIStateMachine(EUIState.Waiting));

   const [message, setMessage] = useState("");
   const [evaluation, setEvaluation] = useState<ISpecificationEvaluation | undefined>(undefined);

   const onLinterModeChange = (_: React.FormEvent<HTMLDivElement>, data: { value: string }) => {
      const next = data.value === ELinterMode.kUserStory ? ELinterMode.kUserStory : ELinterMode.kRequirement;
      setLinterMode(next);
      setEvaluation(undefined);
      state.transition(EApiEvent.Reset);
      setState(new LinterUIStateMachine(EUIState.Waiting));
   };

   async function callServer() : Promise<void> {
      const quickCheckUrl = linterMode === ELinterMode.kUserStory 
         ? 'https://requirementlinterapi.azurewebsites.net/api/QuickCheckUserStory'
         : 'https://requirementlinterapi.azurewebsites.net/api/QuickCheckRequirement';
      const evaluateUrl = linterMode === ELinterMode.kUserStory
         ? 'https://requirementlinterapi.azurewebsites.net/api/EvaluateUserStory'
         : 'https://requirementlinterapi.azurewebsites.net/api/EvaluateRequirement';
      //const quickCheckUrl = linterMode === ELinterMode.kUserStory
      //   ? 'http://localhost:7071/api/QuickCheckUserStory'
      //   : 'http://localhost:7071/api/QuickCheckRequirement';
      //const evaluateUrl = linterMode === ELinterMode.kUserStory 
      //   ? 'http://localhost:7071/api/EvaluateUserStory'
      //   : 'http://localhost:7071/api/EvaluateRequirement';

      if (!message) return;

      const result = await processRequirement({
         quickCheckUrl: quickCheckUrl,
         evaluationUrl: evaluateUrl,
         specification: message,
         updateState: (event: EApiEvent) => {
            state.transition(event);
            setState(new LinterUIStateMachine(state.getState()));
         },
         sessionId: sessionUuid
      });

      if (result)
         setEvaluation(result);
   };
   
   const onDismiss = () => {
      setEvaluation(undefined);
      state.transition(EApiEvent.Reset);
      setState(new LinterUIStateMachine(state.getState()));      
   };

   const onSend = (message_: string) => {
      setMessage(message_);
      callServer();
   };

   const onChange = (message_: string) => {
      setMessage(message_);
      state.transition(EApiEvent.Reset);
      setState(new LinterUIStateMachine(state.getState()));      
   };

   const multilineEditProps: IMultilineEditProps = {
      caption: uiStrings.kSpecificationPreamble,
      placeholder: uiStrings.kSpecificationPlaceholder,
      maxLength: kRequirementMaxLength,
      message: message,
      enabled: state.getState() === EUIState.Waiting,
      fontNameForTextWrapCalculation: kFontNameForTextWrapCalculation,
      defaultHeightLines: 10,
      onSend: onSend,
      onChange: onChange,
   };
      

   let blank = <div></div>;
   let notRequirement = blank;
   let linting = blank;
   let error = blank;
   let success = blank;

   if (state.getState() === EUIState.Linting || state.getState() === EUIState.Checking) {
      linting = <div className={columnElementClasses.root}>
         &nbsp;&nbsp;&nbsp;                  
         <Message intent={MessageIntent.kInfo} title={uiStrings.kInfo} body={uiStrings.kProcessing} dismissable={false} />
         <Spinner/>
      </div>
   }    
   if (state.getState() === EUIState.NotARequirement) {
      notRequirement = <div className={columnElementClasses.root}>
         &nbsp;&nbsp;&nbsp;                  
         <Message intent={MessageIntent.kWarning} title={uiStrings.kWarning} body={uiStrings.kNotASpecification} dismissable={true} onDismiss={onDismiss} />
      </div>
   }  
   if (state.getState() === EUIState.Error) {
      error = <div className={columnElementClasses.root}>
         &nbsp;&nbsp;&nbsp;                  
        <Message intent={MessageIntent.kError} title={uiStrings.kError} body={uiStrings.kServerErrorDescription} dismissable={true} onDismiss={onDismiss} />
      </div>
   }
   if (state.getState() === EUIState.Waiting && evaluation) {
      success = <div className={columnElementClasses.root}>
         &nbsp;&nbsp;&nbsp;                  
         {evaluation.evaluation.split(/(```(?:code|requirement|plaintext)\n[\s\S]*?\n```)/).map((block, index) => {
            // Skip empty blocks
            if (!block.trim()) return null;
            
            // Remove code fences from the text
            const cleanText = block.trim().replace(/^```(?:code|requirement|plaintext)\n/, '').replace(/\n```$/, '');
            
            return (
               <CopyableText 
                  key={index} 
                  placeholder={uiStrings.kReviewPlaceholder} 
                  text={cleanText} 
               />
            );
         })}
      </div>
   }

   return (
      <div className={pageOuterClasses.root}>
         <div className={innerColumnClasses.root}>
            <Text className={textClasses.heading}>{uiStrings.kSpecificationLinterCaption}</Text>
            <Text className={textClasses.centredHint}>{uiStrings.kSpecificationLinterStrapline}</Text>
            <Spacer />
            <div className={columnElementClasses.root}>
               <Label>{kLintAsLabel}</Label>
               <RadioGroup
                  value={linterMode}
                  onChange={onLinterModeChange}
                  layout="horizontal"
               >
                  <Radio value={ELinterMode.kRequirement} label={kRequirementOptionLabel} />
                  <Radio value={ELinterMode.kUserStory} label={kUserStoryOptionLabel} />
               </RadioGroup>
            </div>
            <Spacer />
            <Text className={textClasses.normal}>{uiStrings.kSpecificationLinterDescription}</Text>
            {[uiStrings.kLinks, uiStrings.kRepo].map(markdownLinks => {
               return markdownLinks.split(',').map((link, index) => {
                  // Extract URL and text from markdown format [text](url)
                  const matches = link.match(/\[(.*?)\]\((.*?)\)/);
                  if (matches) {
                     const [_, text, url] = matches;
                     return (
                        <FluentLink key={index} href={url} className={linkClasses.left} target="_blank">{text}</FluentLink>
                     );
                  }
                  return null;
               });
            })}
            <Spacer />
            <MultilineEdit {...multilineEditProps} />
            {notRequirement}
            {linting}
            {error}
            {success}
            <Spacer />
            <Footer />
         </div>
      </div>
   );
}

